/**
 * 道具系统：P/B/POINT/STAR/1UP，炸开后平滑过渡到直线下落 + 磁吸 + POC 全屏吸取。
 * 纯逻辑 + 运行时绘制，node 可直接 import。
 */
import { VIEW_W, VIEW_H, MAX_ITEMS } from '../config.js';
import { Pool } from '../engine/pool.js';
import { randRange, angleTo } from '../engine/math.js';
import { SCORE_CONFIG } from './scoring.js';

/** 磁吸触发半径（px） */
const MAGNET_RADIUS = 64;
/** 磁吸/POC 飞行速度（px/帧 @60fps） */
const MAGNET_SPEED = 8;
/** 拾取半径（px） */
const PICKUP_RADIUS = 12;
/** 上抛初速范围（px/帧 @60fps） */
const TOSS_VX = 1.5;
const TOSS_VY_MIN = 2;
const TOSS_VY_MAX = 4;
/** 下落缓速上限（px/帧 @60fps，vy 缓动逼近值；2.5× 于初版） */
const FALL_MAX = 3.0;
/** 出界回收余量（宽容：道具出屏后仍可被磁吸/POC 吸回，不宜即消） */
const MARGIN = 96;

/** @type {Object<string, {fill: string, label: string}>} 各类型占位绘制样式 */
const TYPE_STYLE = {
  P: { fill: '#e33', label: 'P' },
  B: { fill: '#3c3', label: 'B' },
  POINT: { fill: '#39f', label: '点' },
  STAR: { fill: '#fc3', label: '星' },
  '1UP': { fill: '#f6c', label: '1UP' },
};

function makeItem() {
  return { x: 0, y: 0, vx: 0, vy: 0, type: 'POINT', age: 0, magnet: false };
}

/**
 * 道具管理：生成/散落/物理/磁吸/POC/拾取回调。
 */
export class Items {
  constructor() {
    /** @type {Pool} */
    this.pool = new Pool(MAX_ITEMS, makeItem);
    /** @type {null|((item: object) => void)} 拾取回调（注入 GameState.collectItem） */
    this.onCollect = null;
  }

  /** 当前活跃道具数 */
  get count() {
    return this.pool.activeCount;
  }

  /**
   * 生成一个道具（随机上抛初速）。
   * @param {number} x @param {number} y
   * @param {'P'|'B'|'POINT'|'STAR'|'1UP'} type
   * @returns {object|null}
   */
  spawn(x, y, type) {
    const it = this.pool.obtain();
    if (!it) return null;
    it.x = x;
    it.y = y;
    it.vx = randRange(-TOSS_VX, TOSS_VX);
    it.vy = -randRange(TOSS_VY_MIN, TOSS_VY_MAX);
    it.type = type;
    it.age = 0;
    it.magnet = false;
    return it;
  }

  /**
   * 按掉落表散落道具：drops=[{type, weight}] 按权重抽取，每次调用必掉一个。
   * 需要掉多个时循环调用。
   * @param {number} x @param {number} y
   * @param {{type: string, weight: number}[]} drops
   */
  scatterDrops(x, y, drops) {
    if (!drops || drops.length === 0) return;
    let total = 0;
    for (let i = 0; i < drops.length; i++) total += drops[i].weight;
    let roll = randRange(0, total);
    for (let i = 0; i < drops.length; i++) {
      roll -= drops[i].weight;
      if (roll < 0) {
        this.spawn(x, y, drops[i].type);
        return;
      }
    }
  }

  /**
   * 推进全部道具：重力抛物线 → 磁吸/POC → 拾取判定 → 出界回收。
   * @param {number} dt 秒
   * @param {{playerX: number, playerY: number, pocActive?: boolean, viewH?: number}} ctx
   *   pocActive 一般由 gameScene 按 playerY < SCORE_CONFIG.pocLineY 给出；
   *   不传时本方法按 playerY 自行判断。viewH 供 collectItem 用。
   */
  update(dt, ctx) {
    const k = dt * 60;
    const poc = ctx.pocActive !== undefined
      ? ctx.pocActive
      : ctx.playerY < SCORE_CONFIG.pocLineY;
    const magnetR = ctx.magnetRadius || MAGNET_RADIUS; // 角色磁吸半径（stats）
    this.pool.forEachActive((it) => {
      it.age += dt;
      const dx = ctx.playerX - it.x;
      const dy = ctx.playerY - it.y;
      const d2 = dx * dx + dy * dy;
      if (!it.magnet && (poc || d2 < magnetR * magnetR)) it.magnet = true;
      if (it.magnet) {
        const a = angleTo(it.x, it.y, ctx.playerX, ctx.playerY);
        it.vx = Math.cos(a) * MAGNET_SPEED;
        it.vy = Math.sin(a) * MAGNET_SPEED;
      } else {
        // 炸开 → 平滑直线下落：vx 指数衰减归零，vy 缓动逼近下落速度（无折线）
        it.vx *= Math.max(0, 1 - 4 * dt);
        it.vy += (FALL_MAX - it.vy) * Math.min(1, 3 * dt);
      }
      it.x += it.vx * k;
      it.y += it.vy * k;
      if (d2 < PICKUP_RADIUS * PICKUP_RADIUS) {
        if (this.onCollect) this.onCollect(it);
        this.pool.release(it);
        return;
      }
      if (it.x < -MARGIN || it.x > VIEW_W + MARGIN || it.y > VIEW_H + MARGIN) {
        this.pool.release(it);
      }
    });
  }

  /**
   * 遍历全部活跃道具。
   * @param {(it: object) => void} fn
   */
  forEachActive(fn) {
    this.pool.forEachActive(fn);
  }

  /** 清空全部道具。 */
  clear() {
    this.pool.clear();
  }

  /**
   * 绘制全部道具：优先 Assets.img(`item-${type}`)，
   * 无素材时画带字母的色块（P红/B绿/POINT蓝/STAR黄/1UP粉）。
   * @param {import('../engine/renderer.js').Renderer} renderer
   * @param {import('../engine/assets.js').Assets} [assets]
   */
  draw(renderer, assets) {
    this.pool.forEachActive((it) => {
      const img = assets ? assets.img(`item-${it.type}`) : undefined;
      if (img) {
        renderer.sprite(img, it.x, it.y);
        return;
      }
      const st = TYPE_STYLE[it.type] || TYPE_STYLE.POINT;
      renderer.rect(it.x - 6, it.y - 6, 12, 12, { fill: st.fill, stroke: '#fff' });
      renderer.text(st.label, it.x, it.y - 5, { size: 9, color: '#fff', align: 'center' });
    });
  }
}
